import { ChevronRight, Clock, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * PageContext — Beantwoordt "Waar ben ik?" + "Wat zie ik?" + "Wat vraagt mijn aandacht?"
 *
 * Wordt direct onder de PageToolbar geplaatst.
 *
 * @param {string}   title       - Paginatitel context (bijv. "Risico's beheren")
 * @param {string}   description - Wat zie ik? (bijv. "Hier staan de risico's uit je nulmeting")
 * @param {string}   insight     - Geprioriteerd inzicht (bijv. "4 risico's vereisen directe aandacht")
 * @param {string}   insightType - 'warning' | 'info' | 'success' | 'neutral'
 * @param {string}   testId
 */
export const PageContext = ({ title, description, insight, insightType = 'info', testId = 'page-context' }) => {
  const insightStyles = {
    warning: 'bg-[#FFFBEB] border-[#FDE68A] text-[#92400E]',
    critical: 'bg-[#FEF2F2] border-[#FECACA] text-[#B91C1C]',
    info: 'bg-[#EFF6FF] border-[#BFDBFE] text-[#1D4ED8]',
    success: 'bg-[#ECFDF5] border-[#A7F3D0] text-[#047857]',
    neutral: 'bg-[#F3F4F6] border-[#E5E7EB] text-[#4B5563]',
  };

  return (
    <div className="px-6 py-3 bg-white border-b border-neutral-100" data-testid={testId}>
      <p className="text-sm text-[#4B5563] leading-relaxed">{description}</p>
      {insight && (
        <div className={`mt-2 px-3 py-1.5 rounded-md border text-[11px] font-medium inline-flex items-center gap-1.5 ${insightStyles[insightType]}`} data-testid={`${testId}-insight`}>
          <span className="h-1.5 w-1.5 rounded-full bg-current opacity-60" />
          {insight}
        </div>
      )}
    </div>
  );
};

/**
 * GuidedAction — Het actieblok dat "Wat moet ik nu doen?" beantwoordt
 *
 * Toont één dominante aanbevolen actie met context, uitleg en tijdsindicatie.
 *
 * @param {string}          title       - Actietitel (bijv. "Pak eerst ransomware via de firewall aan")
 * @param {string}          description - Uitleg waarom (bijv. "Dit is een kritiek risico zonder maatregel...")
 * @param {string}          actionLabel - Knoplabel (bijv. "Risico beoordelen")
 * @param {function}        onAction    - Klik handler
 * @param {string}          duration    - Tijdsindicatie (bijv. "Ongeveer 5 minuten")
 * @param {string}          severity    - 'critical' | 'warning' | 'info' | 'success'
 * @param {React.ReactNode} icon        - Optioneel icoon
 * @param {string}          secondaryLabel - Alternatieve actie (bijv. "Later beslissen")
 * @param {function}        onSecondary - Secondary handler
 * @param {string}          testId
 */
export const GuidedAction = ({
  title,
  description,
  actionLabel,
  onAction,
  duration,
  severity = 'info',
  icon: Icon,
  secondaryLabel,
  onSecondary,
  testId = 'guided-action',
}) => {
  const borderMap = {
    critical: 'border-l-[#B91C1C] bg-[#FEF2F2]/30',
    warning: 'border-l-[#D97706] bg-[#FFFBEB]/30',
    info: 'border-l-[#1D4ED8] bg-[#EFF6FF]/20',
    success: 'border-l-[#047857] bg-[#ECFDF5]/20',
  };

  return (
    <div className={`mx-6 my-3 p-4 rounded-lg border border-neutral-200 border-l-[3px] ${borderMap[severity] || borderMap.info}`} data-testid={testId}>
      <div className="flex items-start gap-3">
        {Icon && (
          <div className="h-8 w-8 rounded-lg bg-white border border-neutral-200 flex items-center justify-center shrink-0 mt-0.5">
            <Icon className="h-4 w-4 text-neutral-600" strokeWidth={1.5} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-[#111827]">{title}</h3>
          <p className="text-sm text-[#4B5563] leading-relaxed mt-1">{description}</p>
          <div className="flex items-center gap-3 mt-3">
            <Button size="sm" className="h-7 text-[11px] bg-[#111827] text-white hover:bg-neutral-800" onClick={onAction} data-testid={`${testId}-primary`}>
              {actionLabel}
              <ArrowRight className="h-3 w-3 ml-1.5" strokeWidth={2} />
            </Button>
            {secondaryLabel && onSecondary && (
              <button onClick={onSecondary} className="text-sm text-[#6B7280] hover:text-[#111827] transition-colors" data-testid={`${testId}-secondary`}>
                {secondaryLabel}
              </button>
            )}
            {duration && (
              <span className="text-xs text-[#6B7280] flex items-center gap-1 ml-auto">
                <Clock className="h-3.5 w-3.5" strokeWidth={1.5} />{duration}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * DecisionCard — Helpt de gebruiker een keuze maken met gestructureerde informatie
 *
 * @param {string}  title       - Keuzetitel
 * @param {string}  recommended - Aanbevolen optie
 * @param {string}  reason      - Waarom deze keuze wordt aanbevolen
 * @param {string}  impact      - Impact van de keuze
 * @param {string}  cost        - Benodigde tijd/kosten
 * @param {string}  inaction    - Wat er gebeurt als je niets doet
 * @param {string}  benchmark   - Wat andere organisaties doen
 * @param {boolean} reversible  - Of de beslissing later gewijzigd kan worden
 */
export const DecisionCard = ({
  title,
  recommended,
  reason,
  impact,
  cost,
  inaction,
  benchmark,
  reversible = true,
  testId = 'decision-card',
}) => (
  <div className="p-4 rounded-lg border border-neutral-200 bg-neutral-50/50 space-y-2.5" data-testid={testId}>
    <p className="text-[11px] font-bold text-neutral-800">{title}</p>
    {[
      { label: 'Aanbevolen', value: recommended, highlight: true },
      { label: 'Waarom', value: reason },
      { label: 'Impact', value: impact },
      { label: 'Tijd/kosten', value: cost },
      { label: 'Niets doen', value: inaction },
      { label: 'Benchmark', value: benchmark },
    ].filter(r => r.value).map((row, i) => (
      <div key={i} className="flex items-start gap-2">
        <span className="text-[10px] text-neutral-400 w-20 shrink-0 pt-0.5">{row.label}</span>
        <span className={`text-[11px] leading-relaxed ${row.highlight ? 'font-semibold text-neutral-900' : 'text-neutral-600'}`}>{row.value}</span>
      </div>
    ))}
    {reversible && (
      <p className="text-[10px] text-neutral-400 italic pt-1 border-t border-neutral-200">Deze beslissing kan later worden gewijzigd.</p>
    )}
  </div>
);
